// src/pages/HeuresEnseignement/HeuresEnseignement.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getAllEnseignants } from "../../../services/adherentServices";
import {
  getAllHeures_enseignement,
  deleteHeures_enseignement,
  deleteAllHeures_enseignement,
} from "../../../services/heuresenseignementServices";
import { toast, ToastContainer } from "react-toastify";
import AddHoursModal from "./AddHoursModal";
import UpdateHoursModal from "./UpdateHoursModal";
import Button from "../../../components/Button/Button";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaClock,
  FaCalendarAlt,
} from "react-icons/fa";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const fmt = (h) =>
  parseFloat(h)
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");

export default function HeuresEnseignement() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [enseignants, setEnseignants] = useState([]);
  const [heures, setHeures] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedHeure, setSelectedHeure] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ensData, hData] = await Promise.all([
        getAllEnseignants(),
        getAllHeures_enseignement(),
      ]);
      setEnseignants(Array.isArray(ensData) ? ensData : []);
      setHeures(Array.isArray(hData) ? hData : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const heuresIndex = useMemo(() => {
    const m = new Map();
    for (const h of heures) {
      m.set(`${h.mois}__${h.semaine}__${h.adherent_id}`, h);
    }
    return m;
  }, [heures]);

  const moisList = useMemo(
    () => [...new Set(heures.map((h) => h.mois))].sort(),
    [heures]
  );
  const semainesList = useMemo(
    () => [...new Set(heures.map((h) => h.semaine))].sort(),
    [heures]
  );

  const nomEns = useMemo(() => {
    const o = {};
    enseignants.forEach((e) => (o[e.id] = `${e.nom} ${e.prenom}`));
    return o;
  }, [enseignants]);

  const totalByEns = useMemo(() => {
    const t = {};
    for (const h of heures) {
      t[h.adherent_id] = (t[h.adherent_id] || 0) + parseFloat(h.heures || 0);
    }
    return t;
  }, [heures]);

  const monthlyByEns = useMemo(() => {
    const t = {};
    for (const h of heures) {
      const k = `${h.mois}__${h.adherent_id}`;
      t[k] = (t[k] || 0) + parseFloat(h.heures || 0);
    }
    return t;
  }, [heures]);

  const handleEditClick = (heure) => {
    setSelectedHeure(heure);
    setIsUpdateModalOpen(true);
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try {
      await deleteHeures_enseignement(id);
      toast.success("Heures supprimées.");
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleDeleteAllClick = async () => {
    if (!window.confirm("Supprimer TOUTES les heures d’enseignement ?")) return;
    try {
      await deleteAllHeures_enseignement();
      toast.success("Toutes les heures ont été supprimées.");
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression globale.");
    }
  };

  // Format "YYYY-MM" -> "Septembre 2025"
  const formatMonthFR = (mois) => {
    if (!/^\d{4}-\d{2}$/.test(mois)) return mois;
    const [y, m] = mois.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    const label = d.toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric",
    });
    return label.charAt(0).toUpperCase() + label.slice(1); // capitalise 1ère lettre
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Gestion des heures enseignants"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Header gradient */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaClock />
                </div>
                <div>
                  <p className="text-white text-lg sm:text-xl font-semibold">
                    Synthèse hebdomadaire par enseignant
                  </p>
                  <p className="text-white/90 text-sm">
                    Cliquez sur une cellule pour modifier/supprimer l’entrée.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  text="Ajouter des heures"
                  icon={FaPlus}
                  onClick={() => setIsAddModalOpen(true)}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1"
                />
                <Button
                  text="Supprimer toutes les heures"
                  icon={FaTrash}
                  onClick={handleDeleteAllClick}
                  className="!bg-black/20 !text-white hover:!bg-black/30 !rounded-xl gap-1"
                />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-20"
                  />
                ))}
              </div>
            )}

            {!loading && heures.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucune donnée pour le moment
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Cliquez sur “Ajouter des heures” pour commencer.
                </p>
              </div>
            )}

            {/* TABLE DESKTOP */}
            {!loading && heures.length > 0 && (
              <div className="hidden md:block">
                {/* ✅ Table au bord droit de la section : pleine largeur */}
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40">
                  <table className="w-full md:table-fixed">
                    <thead>
                      <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white w-40">
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt className="opacity-70" /> Semaine
                          </div>
                        </th>
                        {enseignants.map((e) => (
                          <th
                            key={`h-${e.id}`}
                            className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white capitalize"
                          >
                            {e.nom} {e.prenom}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                      {moisList.map((mois) => {
                        const semainesDuMois = semainesList.filter((s) =>
                          heures.some((h) => h.mois === mois && h.semaine === s)
                        );
                        return (
                          <React.Fragment key={`m-${mois}`}>
                            {semainesDuMois.map((semaine) => (
                              <tr
                                key={`${mois}-${semaine}`}
                                className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                              >
                                <td className="px-4 py-3 text-slate-800 dark:text-slate-100">
                                  {semaine}
                                </td>
                                {enseignants.map((e) => {
                                  const entry =
                                    heuresIndex.get(
                                      `${mois}__${semaine}__${e.id}`
                                    ) || null;
                                  return (
                                    <td
                                      key={`${mois}-${semaine}-${e.id}`}
                                      className="px-4 py-3 align-middle"
                                    >
                                      {entry ? (
                                        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-2 py-1">
                                          <span className="font-medium text-slate-900 dark:text-slate-100">
                                            {fmt(entry.heures)}
                                          </span>
                                          <button
                                            className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white"
                                            title="Modifier"
                                            onClick={() =>
                                              handleEditClick(entry)
                                            }
                                          >
                                            <FaEdit />
                                          </button>
                                          <button
                                            className="p-1 text-rose-600 hover:text-rose-700"
                                            title="Supprimer"
                                            onClick={() =>
                                              handleDeleteClick(entry.id)
                                            }
                                          >
                                            <FaTrash />
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="text-slate-400">
                                          –
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}

                            {/* Sous-total mois */}
                            <tr className="bg-slate-100/80 dark:bg-slate-800/60">
                              <td className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                {formatMonthFR(mois)}
                              </td>
                              {enseignants.map((e) => (
                                <td
                                  key={`subtotal-${mois}-${e.id}`}
                                  className="px-4 py-3 text-xs font-bold text-slate-800 dark:text-slate-100"
                                >
                                  {fmt(monthlyByEns[`${mois}__${e.id}`] || 0)}
                                </td>
                              ))}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>

                    {/* Total général */}
                    <tfoot>
                      <tr className="bg-slate-900 text-white">
                        <td className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">
                          Total
                        </td>
                        {enseignants.map((e) => (
                          <td
                            key={`t-${e.id}`}
                            className="px-4 py-3 text-xs font-semibold"
                          >
                            {fmt(totalByEns[e.id] || 0)}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* CARTES MOBILE (inchangé) */}
            {!loading && heures.length > 0 && (
              <div className="md:hidden space-y-4">
                {moisList.map((mois) => {
                  const semainesDuMois = semainesList.filter((s) =>
                    heures.some((h) => h.mois === mois && h.semaine === s)
                  );
                  return (
                    <div
                      key={`m-card-${mois}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40"
                    >
                      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {formatMonthFR(mois)}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          Sous-totaux inclus
                        </span>
                      </div>

                      <div className="p-3 space-y-3">
                        {semainesDuMois.map((semaine) => (
                          <div
                            key={`${mois}-${semaine}-card`}
                            className="rounded-lg border border-slate-200 dark:border-slate-800 p-3"
                          >
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-100 mb-2">
                              <FaCalendarAlt className="opacity-70" />
                              {semaine}
                            </div>
                            <div className="space-y-2">
                              {enseignants.map((e) => {
                                const entry =
                                  heuresIndex.get(
                                    `${mois}__${semaine}__${e.id}`
                                  ) || null;
                                return (
                                  <div
                                    key={`${mois}-${semaine}-${e.id}-row`}
                                    className="flex items-center justify-between gap-3 rounded-lg bg-white/70 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 px-2 py-1.5"
                                  >
                                    <span className="text-xs text-slate-600 dark:text-slate-300">
                                      {nomEns[e.id]}
                                    </span>
                                    {entry ? (
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                          {fmt(entry.heures)}
                                        </span>
                                        <button
                                          className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white"
                                          title="Modifier"
                                          onClick={() => handleEditClick(entry)}
                                        >
                                          <FaEdit />
                                        </button>
                                        <button
                                          className="p-1 text-rose-600 hover:text-rose-700"
                                          title="Supprimer"
                                          onClick={() =>
                                            handleDeleteClick(entry.id)
                                          }
                                        >
                                          <FaTrash />
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-slate-400">–</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}

                        <div className="pt-2 text-right text-xs text-slate-700 dark:text-slate-300">
                          Total général :{" "}
                          <span className="font-bold">
                            {fmt(
                              Object.values(totalByEns).reduce(
                                (a, b) => a + (b || 0),
                                0
                              )
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <ToastContainer />
      </div>

      {/* Modales */}
      <AddHoursModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        refreshData={fetchData}
        enseignants={enseignants}
        heuresEnseignement={heures}
      />
      {selectedHeure && (
        <UpdateHoursModal
          isOpen={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          heure={selectedHeure}
          refreshData={fetchData}
        />
      )}
    </section>
  );
}
